import { Fragment, useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FlatList, StyleSheet } from "react-native";

import {
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
} from "@traveloka/district-bloom";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";

import { FlightPrebookAddOnsQuery, FlightPrebookFormValue } from "../types";
import FlightPrebookPassengerDrawer from "./components/FlightPrebookPassengerDrawer";
import FlightPrebookPassengerField from "./components/FlightPrebookPassengerField";
import {
  FlightPrebookPassengerFormValue,
  FlightPrebookPassengerItemField,
} from "./types";

type Props = {
  addOns: FlightPrebookAddOnsQuery;
};

export default function FlightPrebookPassengers({ addOns }: Props) {
  const [showByIndex, setShowByIndex] = useState<number | null>(null);
  const { CorporateFlightPrebookForm: cr } = useMobileContentResource();

  const { control, getValues, setValue } =
    useFormContext<FlightPrebookFormValue>();

  const adults = useFieldArray({
    control,
    name: "passengers.adults",
  });
  const children = useFieldArray({
    control,
    name: "passengers.children",
  });
  const infants = useFieldArray({
    control,
    name: "passengers.infants",
  });

  const [isTripDomesticID, departureDate] = useWatch({
    control,
    name: ["isTripDomesticID", "departureDate"],
  });

  const passengers: FlightPrebookPassengerItemField[] = useMemo(() => {
    const _adults = adults.fields.map((adult, idx) => ({
      id: adult.id,
      group: "adults" as const,
      index: idx,
    }));
    const _children = children.fields.map((child, idx) => ({
      id: child.id,
      group: "children" as const,
      index: idx,
    }));
    const _infants = infants.fields.map((infant, idx) => ({
      id: infant.id,
      group: "infants" as const,
      index: idx,
    }));
    return [..._adults, ..._children, ..._infants];
  }, [adults, children, infants]);

  const selected = useMemo(() => {
    if (showByIndex === null) return null;

    const passenger = passengers[showByIndex];
    const name = `passengers.${passenger.group}.${passenger.index}` as const;
    return { name, value: getValues(name), index: showByIndex };
  }, [showByIndex, passengers]);

  const onSaveWholePassenger = useCallback(
    (data: FlightPrebookPassengerFormValue) => {
      if (selected === null) return;

      const { name } = selected;

      setValue(name, data, {
        shouldValidate: true,
      });
      setShowByIndex(null);
    },
    [selected]
  );

  const handleEdit = useCallback((rowIndex: number) => {
    setShowByIndex(rowIndex);
  }, []);

  const keyExtractor = useCallback(
    (item: FlightPrebookPassengerItemField) => item.id,
    []
  );
  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: FlightPrebookPassengerItemField;
      index: number;
    }) => (
      <FlightPrebookPassengerField
        id={item.id}
        group={item.group}
        index={item.index}
        onClickEdit={() => handleEdit(index)}
      />
    ),
    [handleEdit, addOns.isLoading]
  );

  return (
    <Fragment>
      <BMView spacing="md">
        <BMText variant="uiHuge">{cr.passengerFormTitle}</BMText>
        {!passengers.length ? (
          <FlatList
            data={[...Array.from({ length: 3 }).map((_, i) => i)]}
            scrollEnabled={false}
            renderItem={() => <PassengerLoadingSkeleton />}
            keyExtractor={(_, index) => `skeleton-${index}`}
            contentContainerStyle={styles.list}
          />
        ) : null}
        {passengers.length ? (
          <FlatList
            data={passengers}
            scrollEnabled={false}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            contentContainerStyle={styles.list}
          />
        ) : null}
      </BMView>
      {selected ? (
        <FlightPrebookPassengerDrawer
          addOns={addOns}
          index={selected.index}
          defaultValues={selected.value}
          departureDate={departureDate}
          isTripDomesticID={isTripDomesticID}
          onSave={onSaveWholePassenger}
          onClose={() => setShowByIndex(null)}
        />
      ) : null}
    </Fragment>
  );
}

function PassengerLoadingSkeleton() {
  return <BMSkeletonLoader height={46} width={"100%"} />;
}

const styles = StyleSheet.create({
  list: {
    gap: BMPrimitives.BMSize.sm,
  },
});
